/**
 * Reimprospatarea tokenului, serializata: o singura cerere de refresh in zbor, in tot browserul.
 *
 * 🔴 De ce exista (2026-09-28). Refresh token-ul se ROTESTE: backendul il consuma si emite altul.
 * Doua cereri cu acelasi token => una castiga, cealalta primeste eroare => frontendul face logout.
 * In PROD, 21-27.09: 18 erori 500 pe `POST /api/auth/refresh-token`, 14 dintre ele exact cursa asta.
 * Cererile concurente veneau din doua locuri:
 *   1. DOUA CAI in acelasi tab — interceptorul din `client.ts` (proactiv / pe 401) si
 *      `handleTokenRefresh` din `auth-provider-v2`, chemat fortat la 10 s pe paginile de asteptare,
 *      fiecare cu propriul POST. „Single-flight"-ul din client.ts nu-l vedea pe al doilea.
 *   2. DOUA TABURI — tokenurile stau in `localStorage`, comun tuturor taburilor, iar ambele
 *      reimprospateaza proactiv cu ~60 s inainte de expirare, deci aproape simultan.
 *
 * Remediul: o singura functie pentru ambele cai (promisiune comuna in tab) si un Web Lock pentru
 * taburi. Dupa ce primeste lock-ul, functia reciteste tokenul: daca alt tab l-a rotit cat a
 * asteptat, il foloseste pe acela in loc sa trimita un token deja consumat.
 *
 * Modul pur, cu dependinte injectate, ca sa fie testabil fara browser (vitest ruleaza pe `node`).
 */

export interface RaspunsRefresh {
    token: string;
    refreshToken: string;
    expiresIn: number;
}

export interface DependinteRefresh {
    /** Citeste tokenurile CURENTE din stocare (nu din starea React, care poate fi veche). */
    citeste: () => { token: string | null; refresh: string | null };
    trimite: (refresh: string) => Promise<RaspunsRefresh>;
    salveaza: (raspuns: RaspunsRefresh) => void;
    /** Exclusivitate intre taburi (Web Locks). Lipsa = doar serializare in tab. */
    cuLock?: <T>(fn: () => Promise<T>) => Promise<T>;
}

export function creeazaRefreshSerializat(deps: DependinteRefresh): () => Promise<string> {
    let inZbor: Promise<string> | null = null;

    return () => {
        if (inZbor) return inZbor;

        const refreshLaCerere = deps.citeste().refresh;

        const executa = async (): Promise<string> => {
            const { token, refresh } = deps.citeste();
            if (!refresh) throw new Error('No refresh token available');
            // Alt tab a rotit tokenul cat asteptam lock-ul: al lui e proaspat emis, deci il folosim.
            // Trimiterea celui vechi ar fi exact cererea care pierde cursa si deconecteaza omul.
            if (refreshLaCerere && refresh !== refreshLaCerere && token) return token;

            const raspuns = await deps.trimite(refresh);
            deps.salveaza(raspuns);
            return raspuns.token;
        };

        inZbor = (deps.cuLock ? deps.cuLock(executa) : executa()).finally(() => {
            inZbor = null;
        });
        return inZbor;
    };
}

/** Web Locks, doar daca browserul le are (Baseline 2022); altfel serializarea ramane doar in tab. */
export function lockIntreTaburi(): DependinteRefresh['cuLock'] {
    if (typeof navigator === 'undefined' || !('locks' in navigator) || !navigator.locks) return undefined;
    // `.then((r) => r)`: tipul DOM al lui `request` e `Promise<Promise<T>>`; la rulare se aplatizeaza.
    return (fn) => navigator.locks.request('ecotic-refresh-token', fn).then((r) => r);
}
